/**
 * Contenido de la AYUDA CONTEXTUAL por pantalla.
 *
 * Es solo texto (sin backend). Para actualizar la ayuda de una pantalla,
 * edita el bloque correspondiente aquí: el componente <AyudaContextual />
 * lo renderiza automáticamente.
 *
 * El flujo de SuMinuta tiene 9 PASOS secuenciales (el "ciclo semanal").
 * Las pantallas de datos maestros / configuración / resumen se marcan como
 * `auxiliar: true` (no son un paso de la secuencia).
 */

export interface ComoEncaja {
  /** Frase corta que ubica la pantalla en el proceso. */
  resumen: string;
  /** Paso anterior (texto). Omitir en el primer paso o en auxiliares. */
  antes?: string;
  /** Paso siguiente (texto). Omitir en el último paso o en auxiliares. */
  despues?: string;
}

export interface AyudaSeccion {
  /** Título que se muestra en el panel de ayuda. */
  titulo: string;
  /** Número de paso dentro del flujo (1..TOTAL_PASOS). */
  paso?: number;
  /** true si la pantalla NO es un paso del flujo (datos maestros, config, resumen). */
  auxiliar?: boolean;
  queMuestra: string[];
  quePuedesHacer: string[];
  comoEncaja: ComoEncaja;
}

/** Cantidad de pasos del flujo principal (el "de Y" en "Paso X de Y"). */
export const TOTAL_PASOS = 9;

/** Mapa numerado del flujo, por si se quiere renderizar la secuencia completa. */
export const FLUJO_PASOS: { n: number; label: string; seccion: string }[] = [
  { n: 1, label: 'Recetas', seccion: 'recetas' },
  { n: 2, label: 'Ingredientes', seccion: 'ingredientes' },
  { n: 3, label: 'Compras', seccion: 'compras' },
  { n: 4, label: 'Lotes', seccion: 'lotes' },
  { n: 5, label: 'Minutas', seccion: 'minutas' },
  { n: 6, label: 'Armar Pedido', seccion: 'armar-pedido' },
  { n: 7, label: 'Gestión de Pedidos', seccion: 'gestion-pedidos' },
  { n: 8, label: 'Cocina', seccion: 'cocina' },
  { n: 9, label: 'Mermas', seccion: 'mermas' },
];

export const AYUDA: Record<string, AyudaSeccion> = {
  // ── PASO 1 ───────────────────────────────────────────────
  recetas: {
    titulo: 'Recetas',
    paso: 1,
    queMuestra: [
      'El catálogo de todos los platos del sistema.',
      'El tipo de cada receta (fondo, guarnición, postre, etc.).',
      'Los ingredientes y cantidades que lleva cada plato.',
    ],
    quePuedesHacer: [
      'Crear, editar o archivar recetas.',
      'Definir qué ingredientes usa cada plato y en qué cantidad.',
      'Buscar recetas por nombre o tipo.',
    ],
    comoEncaja: {
      resumen:
        'Las recetas son la base: definen qué se puede cocinar y qué insumos consume cada plato.',
      despues: 'Paso 2 · Ingredientes (los insumos que estas recetas usan).',
    },
  },

  // ── PASO 2 ───────────────────────────────────────────────
  ingredientes: {
    titulo: 'Ingredientes',
    paso: 2,
    queMuestra: [
      'El inventario de insumos con su categoría y unidad.',
      'El stock actual de cada ingrediente.',
      'El stock mínimo que dispara alertas de reposición.',
    ],
    quePuedesHacer: [
      'Crear y editar ingredientes.',
      'Revisar cuánto stock hay disponible.',
      'Definir el mínimo para que el sistema avise cuándo reponer.',
    ],
    comoEncaja: {
      resumen:
        'Son los insumos con los que se arman las recetas y que la cocina consume.',
      antes: 'Paso 1 · Recetas.',
      despues: 'Paso 3 · Compras (para reponer estos insumos).',
    },
  },

  // ── PASO 3 ───────────────────────────────────────────────
  compras: {
    titulo: 'Compras',
    paso: 3,
    queMuestra: [
      'Las órdenes de compra a proveedores y sus facturas.',
      'Sugerencias de compra según los pedidos próximos y el stock.',
    ],
    quePuedesHacer: [
      'Crear órdenes de compra a proveedores.',
      'Registrar facturas (puedes subir la foto y el sistema la lee).',
      'Al registrar una compra, el stock sube automáticamente.',
    ],
    comoEncaja: {
      resumen: 'Es cómo entra stock nuevo al sistema.',
      antes: 'Paso 2 · Ingredientes.',
      despues: 'Paso 4 · Lotes (el stock que llega queda registrado por lote).',
    },
  },

  // ── PASO 4 ───────────────────────────────────────────────
  lotes: {
    titulo: 'Lotes',
    paso: 4,
    queMuestra: [
      'Cada entrada de stock como un lote independiente.',
      'Cantidad disponible, fecha de vencimiento y origen de cada lote.',
    ],
    quePuedesHacer: [
      'Revisar el stock real por lote y controlar vencimientos.',
      'Entender el consumo: se descuenta del lote más antiguo primero (FIFO).',
    ],
    comoEncaja: {
      resumen:
        'Es el stock disponible "de verdad", desglosado por lote, que la cocina irá consumiendo.',
      antes: 'Paso 3 · Compras.',
      despues: 'Paso 5 · Minutas (planificar el menú con ese stock).',
    },
  },

  // ── PASO 5 ───────────────────────────────────────────────
  minutas: {
    titulo: 'Minutas',
    paso: 5,
    queMuestra: [
      'Los menús semanales planificados (las minutas).',
      'Para cada día y cada tipo de comida, qué receta(s) están asignadas.',
    ],
    quePuedesHacer: [
      'Crear minutas por rango de fechas.',
      'Asignar recetas a cada día y tipo (fondo + guarnición donde corresponde).',
      'Dejar la minuta vigente para que los clientes puedan pedir.',
    ],
    comoEncaja: {
      resumen: 'Es la planificación: define qué se ofrece cada día de la semana.',
      antes: 'Paso 4 · Lotes.',
      despues: 'Paso 6 · Armar Pedido (el cliente pide sobre esta minuta).',
    },
  },

  // ── PASO 6 ───────────────────────────────────────────────
  'armar-pedido': {
    titulo: 'Armar Pedido',
    paso: 6,
    queMuestra: [
      'La grilla semanal de la minuta para un cliente.',
      'Por cada día, sus platos y el estado del pedido de ese día.',
    ],
    quePuedesHacer: [
      'Elegir el cliente y la minuta vigente.',
      'Fijar las cantidades por receta en cada día.',
      'Guardar el borrador y confirmar el pedido del día.',
    ],
    comoEncaja: {
      resumen: 'Es donde nace el pedido: el cliente (o el operador) solicita los platos.',
      antes: 'Paso 5 · Minutas.',
      despues: 'Paso 7 · Gestión de Pedidos (el operador lo revisa y aprueba).',
    },
  },

  // ── PASO 7 ───────────────────────────────────────────────
  'gestion-pedidos': {
    titulo: 'Gestión de Pedidos',
    paso: 7,
    queMuestra: [
      'Todos los pedidos organizados por estado, en pestañas.',
      'Borrador → Solicitado → En cocina → Listo → Despachado.',
    ],
    quePuedesHacer: [
      'Aprobar o rechazar los pedidos solicitados (aprobar = enviar a cocina).',
      'Seguir el avance de cada pedido y ver su detalle.',
      'Filtrar por estado, cliente o fecha (incluye vencidos).',
    ],
    comoEncaja: {
      resumen: 'Es el control del operador: decide qué pedidos pasan a producción.',
      antes: 'Paso 6 · Armar Pedido.',
      despues: 'Paso 8 · Cocina (los pedidos aprobados se preparan).',
    },
  },

  // ── PASO 8 ───────────────────────────────────────────────
  cocina: {
    titulo: 'Cocina',
    paso: 8,
    queMuestra: [
      'La lista de producción del día: qué recetas preparar y cuántas porciones.',
      'El detalle por pedido y si hay stock suficiente para cada receta.',
    ],
    quePuedesHacer: [
      'Iniciar la preparación de cada receta (descuenta el stock real).',
      'Marcar recetas como terminadas.',
      'Al terminar todo, el pedido pasa a Listo y luego se despacha.',
    ],
    comoEncaja: {
      resumen:
        'Es la producción: se cocina, se descuenta inventario y se despacha (paso final del pedido).',
      antes: 'Paso 7 · Gestión de Pedidos.',
      despues: 'Paso 9 · Mermas (ajustes posteriores a la producción).',
    },
  },

  // ── PASO 9 ───────────────────────────────────────────────
  mermas: {
    titulo: 'Mermas',
    paso: 9,
    queMuestra: [
      'El registro de pérdidas o desperdicios de ingredientes.',
      'Lo que se botó, venció o se dañó, con su motivo.',
    ],
    quePuedesHacer: [
      'Registrar una merma indicando ingrediente, cantidad y motivo.',
      'Esto ajusta el stock hacia abajo de forma trazable.',
    ],
    comoEncaja: {
      resumen: 'Cierra el ciclo: corrige el inventario por lo que no llegó a venderse.',
      antes: 'Paso 8 · Cocina.',
      despues: 'El resultado del ciclo se ve en el panel de inicio (Dashboard).',
    },
  },

  // ── AUXILIARES (fuera del flujo numerado) ────────────────
  dashboard: {
    titulo: 'Inicio / Dashboard',
    auxiliar: true,
    queMuestra: [
      'Un resumen del estado del sistema: pedidos, stock y alertas.',
      'Indicadores rápidos del ciclo completo.',
    ],
    quePuedesHacer: [
      'Revisar la situación general de un vistazo.',
      'Entrar rápido a cada módulo del flujo.',
    ],
    comoEncaja: {
      resumen:
        'No es un paso del flujo: es el panel de inicio y resumen de todo el ciclo (lo más cercano a "Informes").',
    },
  },

  proveedores: {
    titulo: 'Proveedores',
    auxiliar: true,
    queMuestra: [
      'La lista de proveedores y sus datos de contacto.',
    ],
    quePuedesHacer: [
      'Crear y editar proveedores.',
    ],
    comoEncaja: {
      resumen:
        'Son datos maestros que alimentan el Paso 3 · Compras. No es un paso de la secuencia.',
    },
  },

  clientes: {
    titulo: 'Clientes',
    auxiliar: true,
    queMuestra: [
      'La lista de clientes y sus datos.',
    ],
    quePuedesHacer: [
      'Crear y editar clientes.',
    ],
    comoEncaja: {
      resumen:
        'Son datos maestros que alimentan el Paso 6 · Armar Pedido. No es un paso de la secuencia.',
    },
  },

  administracion: {
    titulo: 'Administración',
    auxiliar: true,
    queMuestra: [
      'Usuarios, roles y permisos del sistema.',
    ],
    quePuedesHacer: [
      'Gestionar quién accede al sistema y qué puede hacer cada rol.',
    ],
    comoEncaja: {
      resumen: 'Es configuración del sistema. No es un paso del flujo operativo.',
    },
  },

  reportes: {
    titulo: 'Reportes',
    auxiliar: true,
    queMuestra: [
      'Reportes e indicadores del sistema.',
    ],
    quePuedesHacer: [
      'Consultar resultados y exportar información.',
    ],
    comoEncaja: {
      resumen:
        'Vista de resultados del ciclo. No es un paso de la secuencia operativa.',
    },
  },
};
